/* ------------------------------------------------------------------ *
 * Route-level RBAC guard.
 *
 * Runs in `beforeLoad` of the role layout routes so unauthorised users
 * never reach the page loader or component. Roles are read from the
 * `user_roles` table (never from client storage).
 * ------------------------------------------------------------------ */
import { redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { ROLE_HOME, type AppRole } from "@/lib/auth";

/** Which roles may enter each console. Admins may use the Admin console;
 *  Super Admin settings stay exclusive to super admins. */
export const CONSOLE_ROLES: Record<"customer" | "partner" | "admin" | "super_admin", AppRole[]> = {
  customer: ["customer"],
  partner: ["partner"],
  admin: ["admin", "super_admin"],
  super_admin: ["super_admin"],
};

export async function fetchRoles(userId: string): Promise<AppRole[]> {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  return ((data as { role: AppRole }[] | null) ?? []).map((r) => r.role);
}

/** Landing page for a user based on the roles they do hold. */
export function homeForRoles(roles: AppRole[]): string {
  const order: AppRole[] = ["super_admin", "admin", "partner", "customer"];
  const match = order.find((r) => roles.includes(r));
  return match ? ROLE_HOME[match] : "/";
}

/**
 * Guard factory for a console layout route.
 * - Guest → /login (with redirect back)
 * - Signed in without the role → their own dashboard (or map home)
 */
export function guardConsole(consoleKey: keyof typeof CONSOLE_ROLES) {
  const allowed = CONSOLE_ROLES[consoleKey];
  return async ({ location }: { location: { href: string } }) => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    const roles = await fetchRoles(data.user.id);
    if (!roles.some((r) => allowed.includes(r))) {
      throw redirect({ to: homeForRoles(roles), replace: true });
    }
    return { roles };
  };
}
